import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, NgModule, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { getBuildIdQuery, IFacet, IFilterSpotLayout, IMakesFacets } from '@ncg/data';
import { NgbNavModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { IconSpriteModule } from 'ng-svg-icon-sprite';
import { combineLatest, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { SwiperModule } from 'swiper/angular';
import { FavoritesPageLinkComponent } from '../../$lazy-content/components/favorites/favorites-page-link/favorites-page-link.component';

import { filterParams, loadQuickFilterFacets } from '../../+state/products/products.actions';
import { ProductsFacade } from '../../+state/products/products.facade';
import { SettingsService } from '../../core/settings.service';
import { TrackingService } from '../../core/tracking.service';
import { ExpansionPanelModule } from '../../expansion-panel/expansion-panel.module';
import { LogosModule } from '../../logos/logos.module';
import { MyProfileButtonModule } from '../../products/my-profile-button/my-profile-button.component';
import { ProductSearchModule } from '../../products/product-search/product-search.component';
import { moveInAndfadeIAnimation, tileAnimation } from '../../utils/animations/filter.animation';
import { UtilsModule } from '../../utils/utils.module';
import { FilterFacetRangeComponent } from '../filter-facet-range/filter-facet-range.component';
import { FilterFacetToggleItemComponent } from '../filter-facet-toggle/filter-facet-toggle-item.component';
import { FilterFacetToggleComponent } from '../filter-facet-toggle/filter-facet-toggle.component';
import { FilterFacetComponent } from '../filter-facet/filter-facet.component';
import { FilterFacetsComponent } from '../filter-facets/filter-facets.component';
import { FilterSidepanelComponent } from '../filter-sidepanel/filter-sidepanel.component';
import { FilterTabContentComponent } from '../filter-tab-content/filter-tab-content.component';
import { FilterTilesComponent } from '../filter-tiles/filter-tiles.component';
import { FilterTypeIconsComponent } from '../filter-type-icons/filter-type-icons.component';
import { FilterService } from '../filter.service';

@Component({
    selector: 'ncg-filter-root',
    template: `
        <ng-container [ngSwitch]="layout">
            <ng-container *ngSwitchCase="'tileview'">
                <div class="filter-tiles-wrapper container is-full">
                    <ncg-filter-tiles *ngIf="makers?.length" [makersArr]="makers" [linkToUsedCars]="linkToUsedCars"> </ncg-filter-tiles>
                </div>
            </ng-container>
            <ng-container *ngSwitchDefault>
                <div *ngIf="isPartOfFilterPage && makers && makers?.length" class="is-hidden-desktop">
                    <div class="filter-items--mobile">
                        <div class="tabs is-filter-mobile is-marginless" [@moveFadeIn]="makers.length">
                            <ul>
                                <ng-container *ngFor="let make of makers; let i = index; trackBy: trackByFn">
                                    <li class="filter-items__tab" [class.is-active]="active === i" [@item]>
                                        <a [title]="make.key" (click)="updateFilterQuery(make.key)">
                                            <ncg-logos class="filter-facets__tab" [key]="make.key"></ncg-logos>
                                        </a>
                                    </li>
                                </ng-container>
                            </ul>
                        </div>
                    </div>
                </div>

                <div class="filter-items" *ngIf="makers && makers.length; else skeletonTmpl" [ngClass]="{ 'is-hidden-touch': isPartOfFilterPage }">
                    <div class="filter-items__action level-item" *ngIf="showFilterButton">
                        <ncg-my-profile-button></ncg-my-profile-button>
                        <ncg-favorites-page-link
                            [showCount]="true"
                            [linkClass]="'is-rounded is-product-sorting'"
                            [textClass]="'is-hidden'"></ncg-favorites-page-link>
                        <button type="button" class="button is-primary is-rounded" (click)="openFilterPanel()">
                            <span class="icon">
                                <svg-icon-sprite
                                    src="assets/images/sprite-multi.svg#filter"
                                    [viewBox]="'0 0 30 30'"
                                    [width]="'30px'"
                                    [height]="'30px'"
                                    aria-hidden="true"></svg-icon-sprite>
                            </span>
                            <span> {{ 'filter_spot.label_all_filters' | translate }}</span>
                        </button>
                    </div>
                    <div
                        class="tabs is-toggle is-filter is-marginless"
                        [@moveFadeIn]="makers.length"
                        [ngClass]="{ 'is-hidden-touch': isPartOfFilterPage }">
                        <ul ngbNav #nav="ngbNav" [(activeId)]="active" [destroyOnHide]="false">
                            <ng-container *ngFor="let make of makers; let i = index; trackBy: trackByFn">
                                <li [ngbNavItem]="i" class="filter-items__tab" [class.is-active]="active === i" [@item]>
                                    <a ngbNavLink [title]="make.key" (click)="updateFilterQuery(make.key)">
                                        <ncg-logos class="is-flex" [useDefaultText]="true" [key]="make.key" [count]="make.count"></ncg-logos>
                                    </a>
                                    <ng-template ngbNavContent>
                                        <ncg-filter-tab-content
                                            [isPartOfFilterPage]="isPartOfFilterPage"
                                            [isVisible]="active === i"
                                            *ngIf="make.key"
                                            [makeKey]="make.key"
                                            [facetItem]="facetItem"></ncg-filter-tab-content>
                                    </ng-template>
                                </li>
                            </ng-container>
                        </ul>
                    </div>
                    <div class="tabs__content is-relative" [ngClass]="{ 'is-hidden-touch': isPartOfFilterPage }">
                        <div [ngbNavOutlet]="nav"></div>
                    </div>
                </div>
                <ng-template #skeletonTmpl>
                    <div class="filter-items" *ngIf="isFetching">
                        <div class="tabs is-toggle is-filter is-marginless">
                            <ul class="nav">
                                <li class="filter-items__tab nav-item is-active">
                                    <a></a>
                                </li>
                                <li class="filter-items__tab nav-item">
                                    <a></a>
                                </li>
                                <li class="filter-items__tab nav-item">
                                    <a></a>
                                </li>
                            </ul>
                        </div>
                        <div class="tabs__content"></div>
                    </div>
                </ng-template>
            </ng-container>
        </ng-container>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [moveInAndfadeIAnimation, tileAnimation],
    standalone: false,
})
export class FilterRootComponent implements OnInit, OnDestroy {
    @Input() isPartOfFilterPage = false;
    @Input() showFilterButton = false;
    @Input() layout?: IFilterSpotLayout = 'tabview';
    @ViewChild('filterTiles') filterTiles: ElementRef;
    active = 1;
    makers?: IFacet[] = [];
    facetItem?: IMakesFacets;
    isFetching: boolean;
    linkToUsedCars = '';
    buildIdQuery = getBuildIdQuery();

    private readonly unsubscribe = new Subject<void>();

    constructor(
        private readonly cd: ChangeDetectorRef,
        private readonly productsFacade: ProductsFacade,
        private readonly filterService: FilterService,
        private readonly trackingService: TrackingService,
        public readonly settingsService: SettingsService
    ) {}

    ngOnInit(): void {
        this.productsFacade.dispatch(loadQuickFilterFacets());

        this.settingsService
            .get()
            .pipe(take(1), takeUntil(this.unsubscribe))
            .subscribe((settings) => {
                this.linkToUsedCars = settings.usedCarsLink.url;
            });

        if (this.isPartOfFilterPage) {
            this.getFacets();
        }

        this.getIsFetching();
        this.getQuickFilter();
    }

    getQuickFilter() {
        this.productsFacade.quickFilterFacets$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
            this.makers = data?.['make.keyword'];
            this.cd.markForCheck();
        });
    }

    getIsFetching() {
        this.productsFacade.isFetching$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
            this.isFetching = data;
            this.cd.markForCheck();
        });
    }

    getFacets() {
        combineLatest([this.productsFacade.filterParams$, this.productsFacade.facets$])
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(([params, facets]) => {
                if (!facets) {
                    return;
                }

                let selectedMakeKey: string | null | undefined = facets['make.keyword']?.find((item) => item.selected)?.key;

                // look for selected make in params if not found in facets$
                if (!selectedMakeKey && params && params['make.keyword']) {
                    selectedMakeKey = params['make.keyword'];
                }

                this.active = facets['make.keyword']?.findIndex((item) => item.key === selectedMakeKey) ?? 0;

                this.facetItem = {
                    'make.keyword': selectedMakeKey ?? '',
                    'bodyTypes': facets?.['bodytypes.keyword'],
                    'series': selectedMakeKey?.toLowerCase() === 'bmw' ? facets?.['series.keyword'] : undefined,
                };

                this.cd.markForCheck();
            });
    }

    updateFilterQuery(facetKey?: string | null) {
        this.productsFacade.dispatch(
            filterParams({
                filterParams: {
                    ['make.keyword']: [facetKey],
                    take: '24',
                },
                resetKey: true,
                resetFilter: true,
            })
        );

        if (facetKey) {
            // track only selected facet
            this.trackingService.trackFilter(facetKey, 'make.keyword');
        }
    }

    openFilterPanel() {
        this.filterService.openFilterPanel();
    }

    trackByFn(index: number, item: any) {
        return item.key || index;
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
@NgModule({
    declarations: [
        FilterRootComponent,
        FilterTabContentComponent,
        FilterSidepanelComponent,
        FilterFacetsComponent,
        FilterFacetComponent,
        FilterFacetRangeComponent,
        FilterFacetToggleComponent,
        FilterFacetToggleItemComponent,
        FilterTypeIconsComponent,
        FilterTilesComponent,
    ],
    imports: [
        CommonModule,
        NgbNavModule,
        BrowserAnimationsModule,
        RouterModule,
        TranslateModule.forChild(),
        ExpansionPanelModule,
        FormsModule,
        ReactiveFormsModule,
        UtilsModule,
        LogosModule,
        SwiperModule,
        IconSpriteModule,
        MyProfileButtonModule,
        ProductSearchModule,
        FavoritesPageLinkComponent,
    ],
    exports: [FilterRootComponent, FilterFacetsComponent],
})
export class FilterRootComponentModule {}
